import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import RegisterLink from '../../../components/RegisterLink';
import { LinkedText } from '../../../components/LinkedText';
import footerLicensePaymentBanner from '../../../assets/footer-license-payment-banner.png';

type FooterLink = {
  en: string;
  bm: string;
  to?: string;
  source?: string;
};

const footerCols: Array<{
  en_title: string;
  bm_title: string;
  links: FooterLink[];
}> = [
  {
    en_title: 'Quick Links',
    bm_title: 'Pautan Pantas',
    links: [
      { en: 'About MrCuci', bm: 'Tentang MrCuci', to: '/about/' },
      { en: 'Bonuses', bm: 'Bonus', to: '/bonus/' },
      { en: 'Hot Games', bm: 'Permainan', to: '/games/' },
      { en: 'FAQ', bm: 'FAQ', to: '/faq/' },
    ],
  },
  {
    en_title: 'Support',
    bm_title: 'Sokongan',
    links: [
      { en: 'FAQ', bm: 'Soalan Lazim', to: '/faq/' },
      { en: 'Terms & Conditions', bm: 'Terma & Syarat', to: '/terms/' },
      { en: 'Responsible Gaming', bm: 'Permainan Bertanggungjawab', to: '/responsible-gaming/' },
    ],
  },
];

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer style={{ background: 'linear-gradient(180deg, #0d0101 0%, #080000 100%)' }}>
      <div className="border-t" style={{ borderColor: 'rgba(212,160,23,0.15)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-1">
              <Link to="/" className="flex items-center mb-4 cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/0922d8cb6b39a31ad70c8c729d5dd75a.png"
                  alt="MrCuci Malaysia Online Casino"
                  className="h-14 md:h-16 w-auto object-contain object-top"
                />
              </Link>
              <p className="text-gray-500 text-xs leading-relaxed mb-4">
                <LinkedText>{t(
                  "MrCuci is Malaysia's #1 trusted online casino. 5,000+ games, instant withdrawals, and unmatched bonuses for 500,000+ Malaysian players.",
                  "MrCuci ialah kasino dalam talian #1 Malaysia yang dipercayai. 5,000+ permainan, pengeluaran segera, dan bonus terbaik untuk 500,000+ pemain Malaysia."
                )}</LinkedText>
              </p>

              <div className="flex gap-2">
                {[
                  {
                    icon: 'ri-facebook-fill',
                    label: 'Facebook',
                    href: 'https://www.facebook.com/people/Mrcuc1-Online-Gaming/61550495784524/',
                  },
                  { icon: 'ri-telegram-fill', label: 'Telegram', href: 'https://t.me/mrcuci_huat' },
                  {
                    icon: 'ri-whatsapp-line',
                    label: 'WhatsApp',
                    href: 'https://api.whatsapp.com/send/?phone=60123456789&text&type=phone_number&app_absent=0',
                  },
                ].map((s) => (
                  <a
                    key={s.icon}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    aria-label={s.label}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:opacity-80 transition-opacity cursor-pointer"
                    style={{ background: 'linear-gradient(135deg, rgba(204,17,0,0.3), rgba(212,160,23,0.2))', border: '1px solid rgba(212,160,23,0.2)' }}
                  >
                    <i className={`${s.icon} text-[#d4a017] text-sm`} />
                  </a>
                ))}
              </div>
            </div>

            {footerCols.map((col) => (
              <div key={col.en_title}>
                <h4 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                  <span
                    className="w-1 h-4 rounded-full"
                    style={{ background: 'linear-gradient(180deg, #cc1100, #d4a017)' }}
                  />
                  {t(col.en_title, col.bm_title)}
                </h4>
                <ul className="space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.en}>
                      {'to' in link && link.to ? (
                        <Link
                          to={link.to}
                          className="text-gray-500 hover:text-[#d4a017] text-xs transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 group"
                        >
                          <span className="w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-arrow-right-s-line text-[#d4a017]" />
                          </span>
                          {t(link.en, link.bm)}
                        </Link>
                      ) : (
                        <RegisterLink
                          source={link.source ?? 'footer_link'}
                          label={t(link.en, link.bm)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-[#d4a017] text-xs transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 group"
                        >
                          <span className="w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-arrow-right-s-line text-[#d4a017]" />
                          </span>
                          {t(link.en, link.bm)}
                        </RegisterLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-[#d4a017]/10">
            <img
              src={footerLicensePaymentBanner}
              alt={t('Gaming license, certification and payment methods', 'Lesen permainan, pensijilan dan kaedah pembayaran')}
              className="w-full h-auto max-w-2xl rounded-xl border border-white/10"
            />
          </div>
        </div>

        <div className="border-t px-4 md:px-6 py-5" style={{ borderColor: 'rgba(212,160,23,0.08)' }}>
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-xs text-center md:text-left">
              &copy; 2026{' '}
              <Link to="/" className="text-gray-500 hover:text-[#d4a017] transition-colors cursor-pointer">
                MrCuci
              </Link>
              {t('. All rights reserved. 18+ only. Please gamble responsibly.', '. Hak cipta terpelihara. 18+ sahaja. Sila berjudi dengan bertanggungjawab.')}
            </p>
          </div>
          <div className="max-w-7xl mx-auto mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <p className="text-gray-700 text-xs text-center leading-relaxed">
              {t(
                '18+ only. Online gambling involves financial risk. Please play responsibly. ',
                '18+ sahaja. Perjudian dalam talian melibatkan risiko kewangan. Sila bermain dengan bertanggungjawab. '
              )}
              <Link to="/responsible-gaming/" className="text-gray-500 hover:text-[#d4a017] transition-colors cursor-pointer">
                {t('MrCuci Responsible Gaming', 'Permainan Bertanggungjawab MrCuci')}
              </Link>
              {' · '}
              <Link to="/terms/" className="text-gray-500 hover:text-[#d4a017] transition-colors cursor-pointer">
                {t('Terms & Conditions', 'Terma & Syarat')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
